import * as Apollo from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import { Either, ReadonlyArray } from "@morphism/fp";
import isEqual from "lodash/isEqual";
import React from "react";

import { Asset, Assets } from "~/Asset";
import { Company } from "~/Company";
import { Distance } from "~/Distance";
import type { Driver } from "~/Driver";
import { Graph, gql } from "~/Graph";
import { Errors } from "~/Graph/Error";
import { Driver__Shift__Connection } from "~/Graph/Generated/Types";
import { useIsFirstRender } from "~/Hooks";
import { ID } from "~/ID";
import { Time } from "~/Time";

export type Shift = Time.Interval &
  Partial<Distance> & { readonly assets: Assets };

export namespace Shift {
  export type Fragment = Graph.Generated.ShiftFragment;
  export namespace Fragment {
    export const document = () => gql`
      ${Asset.Fragment.document()}
      ${Graph.Errors.Fragment.document()}

      fragment Shift on Driver__Shift {
        startDate
        endDate
        isOngoing
        hasDriveTime
        driveDistance
        vehicles {
          ... on Asset {
            ...Asset
          }
          ... on Error {
            ...Error
          }
        }
      }
    `;

    export const decode = (encoded: Fragment): Shift => ({
      interval: {
        start: { dateTime: new Date(encoded.startDate).valueOf() },
        ...(!encoded.isOngoing && {
          stop: { dateTime: new Date(encoded.endDate).valueOf() },
        }),
      },
      ...(encoded.hasDriveTime && {
        distance: { miles: encoded.driveDistance },
      }),
      assets: pipe(
        encoded.vehicles ?? [],
        ReadonlyArray.map((asset) =>
          asset && "id" in asset
            ? Either.right(Asset.Fragment.decode(asset))
            : Either.left(Error(asset?.message))
        ),
        ReadonlyArray.rights
      ),
    });
  }

  export namespace Node {
    export type Fragment = Graph.Generated.Driver__Shift__Node;

    export namespace Fragment {
      export const document = () => gql`
        ${Shift.Fragment.document()}

        fragment DriverShifts on Driver__Shift__Node {
          id
          shifts {
            ...Shift
          }
        }
      `;
    }

    export type DriverShifts = ID & { readonly shifts: readonly Shift[] };

    export const decode = (encoded: Fragment): DriverShifts => ({
      id: encoded.id,
      shifts: (encoded.shifts ?? []).map((shift) =>
        Shift.Fragment.decode(shift)
      ),
    });
  }
}

export type Shifts = { readonly [driverID: string]: readonly Shift[] };
export namespace Shifts {
  export type UseWithMoreArgs = Time.Interval.Stopped & {
    readonly reportingLocationIDs?: readonly number[];
    readonly branchIDs?: Company.Branch.Branches.BranchIDs;
    readonly includeNoBranch?: boolean;
    // eslint-disable-next-line functional/prefer-readonly-type
    readonly driverIDs?: string[];
    readonly last?: number;
    readonly before?: string;
    readonly refetch?: boolean;
    readonly first?: number;
    readonly after?: string;
  };

  export const useWithMore = ({
    interval,
    reportingLocationIDs = [],
    branchIDs = [],
    driverIDs = [],
    last,
    before,
    first,
    after,
    refetch = false,
  }: UseWithMoreArgs) => {
    const { start, stop } = interval;
    const companyID = Company.use().company.id;

    const branchFilterValues = Company.Branch.Branches.Filter.parseValues(
      branchIDs
    );

    const fetchVariables = {
      variables: {
        companyID,
        start: Time.DateTime.toISO(start),
        stop: Time.DateTime.toISO(stop),
        reporting_location_ids: reportingLocationIDs.map((id) => `${id}`),
        branchIDs: branchFilterValues.branchIDs.map(String),
        includeNoBranch: branchFilterValues.includeNoBranch,
        driverIDs,
        last,
        before,
        first,
        after,
      },
    };

    const fetchPolicy =
      fetchVariables.variables.driverIDs.length > 0 || refetch
        ? "no-cache"
        : "cache-and-network";

    const {
      data: dataOrError,
      previousData,
      loading,
      fetchMore,
      error,
    } = Graph.useQuery<Query, Graph.Generated.ShiftsQueryVariables>(
      Query.document(),
      {
        ...fetchVariables,
        fetchPolicy,
        nextFetchPolicy: fetchPolicy,
        returnPartialData: refetch,
      }
    );

    let data: Driver__Shift__Connection | Errors = Connection.emptyConnection();
    let connection = Connection.emptyConnection();
    if (!loading) {
      data = pipe(dataOrError ?? previousData, (data) =>
        data ? Query.decode(data) : Connection.emptyConnection()
      );
      connection = !("graphError" in data)
        ? data
        : Connection.emptyConnection();
    }
    const graphError = "graphError" in data ? data : undefined;

    return {
      loading,
      fetchMore,
      fetchVariables,
      connection,
      ...Graph.Errors.onError(error, graphError),
    };
  };

  export type Query = Graph.Generated.ShiftsQuery;
  export namespace Query {
    export type Variables = Graph.Generated.ShiftsQueryVariables;

    export const document = () => gql`
      ${Graph.Errors.Fragment.document()}
      ${Shift.Node.Fragment.document()}
      ${Shift.Fragment.document()}

      query Shifts(
        $companyID: ID!
        $start: DateTime!
        $stop: DateTime!
        $reporting_location_ids: [String]
        $branchIDs: [String!]
        $includeNoBranch: Boolean
        $driverIDs: [String!]
        $last: Int
        $before: String
        $first: Int
        $after: String
      ) {
        companies(id: $companyID) {
          ... on Company {
            id
            shifts {
              byDateRange(
                input: {
                  from: $start
                  to: $stop
                  reporting_location_ids: $reporting_location_ids
                  branchIDs: $branchIDs
                  includeNoBranch: $includeNoBranch
                  driverIDs: $driverIDs
                }
                last: $last
                before: $before
                first: $first
                after: $after
              ) {
                ... on Driver__Shift__Connection {
                  totalCount
                  pageInfo {
                    hasPreviousPage
                    hasNextPage
                    startCursor
                    endCursor
                  }
                  edges {
                    cursor
                    node {
                      ...DriverShifts
                    }
                  }
                }
                ... on Error {
                  ...Error
                }
              }
            }
          }
        }
      }
    `;

    export const typePolicies = (): Apollo.TypePolicies => ({
      Driver__Shift__ByCompanyQueries: {
        fields: {
          byDateRange: relayStylePagination(),
        },
      },
    });

    export type useProps = Time.Interval.Stopped & {
      readonly reportingLocationIDs: readonly number[];
      readonly branchIDs: Company.Branch.Branches.BranchIDs;
      readonly driver?: Driver;
    };

    export const use = ({
      interval,
      reportingLocationIDs = [],
      branchIDs = [],
      driver = undefined,
    }: useProps) => {
      const [lastInterval, setLastInterval] = React.useState(interval);
      const [isFetchingMore, setIsFetchingMore] = React.useState(true);
      const [lastCursor, setLastCursor] = React.useState("initial");
      const [
        lastReportingLocationIDs,
        setLastReportingLocationIDs,
      ] = React.useState(reportingLocationIDs);

      const [lastBranchIDs, setLastBranchIDs] = React.useState(branchIDs);

      const driverID = driver ? driver.id : undefined;

      React.useEffect(() => {
        if (!isEqual(reportingLocationIDs, lastReportingLocationIDs)) {
          setLastReportingLocationIDs(reportingLocationIDs);
        }
      }, [reportingLocationIDs]);

      React.useEffect(() => {
        if (!isEqual(branchIDs, lastBranchIDs)) {
          setLastBranchIDs(branchIDs);
        }
      }, [branchIDs]);

      const refetch =
        lastCursor === "refetch" ||
        useIsFirstRender() ||
        !isEqual(interval, lastInterval) ||
        !isEqual(reportingLocationIDs, lastReportingLocationIDs) ||
        !isEqual(branchIDs, lastBranchIDs);

      const { connection, loading, fetchMore, errors, warnings } = useWithMore({
        interval,
        reportingLocationIDs,
        branchIDs,
        driverIDs: driverID ? [driverID] : [],
        first: 400,
        refetch,
      });

      React.useEffect(() => {
        if (!isEqual(interval, lastInterval)) {
          setLastInterval(interval);
        }
      }, [interval]);

      React.useEffect(() => {
        if (warnings) {
          Graph.Errors.displayMessage(warnings);
        }
      }, [warnings]);

      const { edges, pageInfo } = connection;

      const shifts = React.useMemo(
        () => pipe(edges ?? [], Connection.decodeEdges),
        [edges]
      );

      React.useEffect(() => {
        setIsFetchingMore((pageInfo && pageInfo.hasNextPage) || loading);
        if (
          lastCursor !== "refresh" &&
          pageInfo.hasNextPage &&
          pageInfo.endCursor &&
          pageInfo.endCursor !== lastCursor
        ) {
          setLastCursor(pageInfo.endCursor);
          setTimeout(() => {
            void fetchMore({
              variables: {
                interval,
                reporting_location_ids: reportingLocationIDs.map(
                  (id) => `${id}`
                ),
                branchIDs: branchIDs.map(String),
                driverIDs: driverID ? [driverID] : undefined,
                first: 1000,
                after: pageInfo.endCursor ?? undefined,
              },
            });
          });
        }
      }, [pageInfo.hasNextPage, pageInfo.endCursor, loading]);

      return {
        shifts,
        loading,
        isFetchingMore,
        errors,
      };
    };

    export const decode = (
      query: Query
    ): Graph.Generated.Driver__Shift__Connection | Graph.Errors => {
      const { companies } = query;
      if (companies && "shifts" in companies) {
        if (companies?.shifts?.byDateRange) {
          if (
            companies.shifts?.byDateRange?.__typename ===
            "Driver__Shift__Connection"
          ) {
            return (companies.shifts?.byDateRange ??
              Connection.emptyConnection()) as Graph.Generated.Driver__Shift__Connection;
          } else if ("message" in companies?.shifts?.byDateRange) {
            return Graph.Errors.Fragment.decode(companies.shifts?.byDateRange);
          }
        }
      }
      return Connection.emptyConnection();
    };
  }

  export namespace Connection {
    export const emptyConnection = (): Graph.Generated.Driver__Shift__Connection => ({
      __typename: "Driver__Shift__Connection",
      totalCount: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
      edges: undefined,
    });

    export const decodeEdges = (
      edges: readonly Graph.Generated.Driver__Shift__Edge[] | undefined
    ): Shifts => {
      return (edges ?? []).reduce(
        (accumulator, edge) => ({
          ...accumulator,
          ...{ [edge.node.id]: [...Shift.Node.decode(edge.node).shifts] },
        }),
        {}
      );
    };
  }
}
